import { useState } from 'react'
import { CheckCircle2, XCircle, Edit3, Shield } from 'lucide-react'
import { fetchWithAuth } from '@/lib/fetchWithAuth'
import type { ApprovalBlock } from '@/types/blocks'

interface InlineApprovalProps {
  block: ApprovalBlock
}

/**
 * HITL approval card rendered inline in chat.
 * Shows action description with Approve / Reject / Modify buttons.
 * After decision, becomes read-only showing the outcome.
 */
export function InlineApproval({ block }: InlineApprovalProps) {
  const { request_id, action, approver_role, message } = block.data
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected' | 'modified'>('pending')
  const [loading, setLoading] = useState(false)
  const [showModify, setShowModify] = useState(false)
  const [modifiedText, setModifiedText] = useState(action)
  const [reason, setReason] = useState('')

  const decide = async (decision: 'approved' | 'rejected' | 'modified') => {
    setLoading(true)
    try {
      const endpoint = decision === 'approved' ? 'approve'
        : decision === 'rejected' ? 'reject'
        : 'modify'
      const body: Record<string, unknown> = { reason: reason || '' }
      if (decision === 'modified') body.modified_action = { query: modifiedText }

      await fetchWithAuth(`/api/approvals/${request_id}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      setStatus(decision)
    } catch (err) {
      console.error('Approval decision failed:', err)
    } finally {
      setLoading(false)
    }
  }

  if (status !== 'pending') {
    const statusColors = {
      approved: 'border-green-500/30 bg-green-500/5',
      rejected: 'border-red-500/30 bg-red-500/5',
      modified: 'border-blue-500/30 bg-blue-500/5',
    }
    const statusIcons = {
      approved: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      rejected: <XCircle className="h-4 w-4 text-red-600" />,
      modified: <Edit3 className="h-4 w-4 text-blue-600" />,
    }
    return (
      <div className={`rounded-lg border p-3 ${statusColors[status]}`}>
        <div className="flex items-center gap-2 text-sm font-medium">
          {statusIcons[status]}
          <span className="capitalize">{status}</span>
          {reason && <span className="text-muted-foreground font-normal">— {reason}</span>}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{message}</p>
          {approver_role && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Requires: <span className="font-medium">{approver_role}</span> approval
            </p>
          )}
        </div>
      </div>

      <pre className="text-xs bg-muted/50 rounded p-2 overflow-x-auto whitespace-pre-wrap">
        {action}
      </pre>

      {showModify && (
        <div className="space-y-2">
          <textarea
            value={modifiedText}
            onChange={(e) => setModifiedText(e.target.value)}
            className="w-full text-xs bg-background border rounded p-2 min-h-[60px] resize-y"
            placeholder="Modify the action..."
          />
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full text-xs bg-background border rounded p-2"
            placeholder="Reason (optional)"
          />
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={() => decide('approved')}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Approve
        </button>
        <button
          onClick={() => decide('rejected')}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          <XCircle className="h-3.5 w-3.5" />
          Reject
        </button>
        <button
          onClick={() => setShowModify(!showModify)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border hover:bg-muted disabled:opacity-50 transition-colors"
        >
          <Edit3 className="h-3.5 w-3.5" />
          Modify
        </button>
        {showModify && (
          <button
            onClick={() => decide('modified')}
            disabled={loading || !modifiedText.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Submit Modified
          </button>
        )}
      </div>
    </div>
  )
}
